"use client"

import { useState, useEffect, useRef } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { Container, Title, Text, Paper, Alert, Center, Stack, Button, TextInput, TbAlertCircle, TbCheck, SocialLoginButtons } from "@forestsync/ui"
import { LoginForm } from "@forestsync/ui"
import { useTranslations } from "next-intl"
import { loginAction, loginWithOAuthAction, sendMagicLinkAction } from "@/actions/auth"
import { createBrowserClient } from "@forestsync/auth/browser"
import { safeRedirectPath } from "@forestsync/lib/validation"
import Link from "next/link"

export default function LoginPage() {
  const t = useTranslations("auth")
  const searchParams = useSearchParams()
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const postLoginStatusRef = useRef<string | null>(null)
  const [mode, setMode] = useState<"password" | "magicLink">("password")
  const [magicLinkStatus, setMagicLinkStatus] = useState<"idle" | "loading" | "sent" | "error">("idle")
  const [magicLinkMessage, setMagicLinkMessage] = useState<string | null>(null)
  const sessionExpired = searchParams.get("reason") === "expired"

  useEffect(() => {
    const supabase = createBrowserClient()
    // getUser (server-verified), not getSession: a dead cookie session would otherwise
    // bounce the user to `next`, where proxy.ts sends them straight back here.
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        // Sanitize to a relative path; blocks //evil.com and /\evil.com backslash bypass.
        const safeNext = safeRedirectPath(searchParams.get("next"), "/dashboard")
        router.replace(safeNext)
      }
    })
  }, [searchParams, router])

  useEffect(() => {
    const code = searchParams.get("error")
    const desc = searchParams.get("error_description")
    if (!code && !desc) return

    const errorMessages: Record<string, string> = {
      server_error: t("oauthServerError"),
      oauth_exchange_failed: t("unknownError"),
      oauth_failed: t("unknownError"),
      oauth_login_failed: t("unknownError"),
      verification_failed: t("unknownError"),
      missing_auth_code: t("unknownError"),
      invalid_oauth_provider: t("unknownError"),
    }

    const decoded = code ? decodeURIComponent(code) : ""
    setError(errorMessages[decoded] ?? (desc ? decodeURIComponent(desc) : t("unknownError")))
  }, [searchParams, t])

  const handleLogin = async (email: string, pass: string) => {
    setError(null)

    // Step 1: client-side sign-in — sets auth cookies directly in the browser
    // (avoids Amplify/CloudFront stripping Set-Cookie from Server Action POST responses)
    const supabase = createBrowserClient()
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password: pass })
    if (authError) {
      setError(authError.message)
      throw new Error(authError.message)
    }

    // Step 2: server-side post-login work (ensureAuthUserRecord + status lookup)
    const result = await loginAction()

    if ("error" in result) {
      setError(result.error)
      throw new Error(result.error)
    }

    if (result.status) postLoginStatusRef.current = result.status
  }

  const handleSendMagicLink = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setMagicLinkStatus("loading")
    setMagicLinkMessage(null)

    const formData = new FormData(e.currentTarget)
    const result = await sendMagicLinkAction(formData)

    if (result.error) {
      setMagicLinkStatus("error")
      setMagicLinkMessage(result.error)
    } else {
      setMagicLinkStatus("sent")
      setMagicLinkMessage(t("magicLinkSuccess"))
    }
  }

  const handleSuccess = () => {
    if (postLoginStatusRef.current === "profile_incomplete" || postLoginStatusRef.current === "pending") {
      window.location.href = "/onboarding"
      return
    }
    if (postLoginStatusRef.current === "pending_stripe_kyc") {
      window.location.href = "/onboarding/stripe"
      return
    }
    window.location.href = safeRedirectPath(searchParams.get("next"), "/dashboard")
  }

  return (
    <Container size="xs" py="xl">
      <Center mb="xl">
        <div>
          <Title order={1} ta="center">{t("loginTitle")}</Title>
          <Text c="dimmed" ta="center" mt="sm">
            {t("loginSubtitle")}
          </Text>
        </div>
      </Center>

      <Paper withBorder shadow="md" p="xl" radius="md">
        {error ? (
          <Alert icon={<TbAlertCircle size={16} />} color="red" mb="md" variant="light">
            {error}
          </Alert>
        ) : sessionExpired && (
          <Alert icon={<TbAlertCircle size={16} />} color="blue" mb="md" variant="light">
            {t("sessionExpired")}
          </Alert>
        )}

        {mode === "password" ? (
          <>
            <LoginForm
              onLogin={handleLogin}
              onSuccess={handleSuccess}
              translations={{
                email: t("email"),
                password: t("password"),
                signIn: t("signInButton"),
                signingIn: t("signingIn"),
                enterEmail: t("enterEmail"),
                enterPassword: t("enterPassword")
              }}
            />

            <Text ta="right" size="sm" mt="xs">
              <Text component={Link} href="/forgot-password" c="blue">
                {t("forgotPasswordLink")}
              </Text>
            </Text>

            <Text ta="center" size="sm" mt="sm">
              <Text component="button" type="button" c="blue" onClick={() => setMode("magicLink")} className="bg-transparent border-none cursor-pointer">
                {t("useMagicLinkInstead")}
              </Text>
            </Text>
          </>
        ) : magicLinkStatus === "sent" ? (
          <Stack align="center" gap="md">
            <Alert icon={<TbCheck size={16} />} color="green" variant="light" w="100%">
              {magicLinkMessage}
            </Alert>
            <Text
              component="button"
              type="button"
              c="blue"
              size="sm"
              onClick={() => { setMode("password"); setMagicLinkStatus("idle") }}
              className="bg-transparent border-none cursor-pointer"
            >
              {t("backToLogin")}
            </Text>
          </Stack>
        ) : (
          <form onSubmit={handleSendMagicLink}>
            <Stack gap="md">
              <Text size="sm" c="dimmed">{t("magicLinkSubtitle")}</Text>
              {magicLinkStatus === "error" && (
                <Alert icon={<TbAlertCircle size={16} />} color="red" variant="light">
                  {magicLinkMessage}
                </Alert>
              )}
              <TextInput name="email" type="email" label={t("email")} placeholder={t("enterEmail")} required />
              <Button type="submit" fullWidth loading={magicLinkStatus === "loading"}>
                {magicLinkStatus === "loading" ? t("magicLinkSending") : t("magicLinkButton")}
              </Button>
              <Text ta="center" size="sm">
                <Text component="button" type="button" c="blue" onClick={() => setMode("password")} className="bg-transparent border-none cursor-pointer">
                  {t("usePasswordInstead")}
                </Text>
              </Text>
            </Stack>
          </form>
        )}

        <SocialLoginButtons
          oauthAction={loginWithOAuthAction}
          translations={{
            orContinueWith: t("orContinueWith"),
            signInWithGoogle: t("signInWithGoogle"),
            signInWithMicrosoft: t("signInWithMicrosoft"),
          }}
        />

        <Text ta="center" mt="xl" size="sm">
          {t("noAccount")}{" "}
          <Text component={Link} href="/register" c="blue" fw={500}>
            {t("registerLink")}
          </Text>
        </Text>
      </Paper>
    </Container>
  )
}
